// src/components/store/QuickAddressComponents/AddressFormForPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@radix-ui/themes";
import { Loader, MapPin, MapPinIcon, TrashIcon } from "lucide-react";
import { useUserAddresses } from "../../../hooks/useUserAddresses";
import { useGoogleMaps } from "../../../hooks/useGoogleMaps";
import { formatPrice } from "../../../scripts/generalScript";
import primaryCoveragePolygon from "../../pages/coverageComponents/perimeter.json";
import toast from "react-hot-toast";
import DividerComponent from "../../ui/DividerComponent";

// Constantes de zonas de delivery (mismo sistema que useAddress)
const DELIVERY_ZONES = {
  1: {
    price: 0,
    label: "Entrega gratis",
    bgColor: "bg-green-100",
    textColor: "text-green-700",
  },
  2: {
    price: 3500,
    label: "Entrega $3.500",
    bgColor: "bg-yellow-100",
    textColor: "text-yellow-700",
  },
  3: {
    price: 7000,
    label: "Entrega $7.000",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
  },
};

// Componente Badge local
const Badge = ({ text, bgColor, textColor }) => (
  <div
    className={`w-fit px-2 py-1 ${bgColor} rounded-md justify-center items-center inline-flex`}
  >
    <div
      className={`${textColor} text-sm md:text-base font-normal leading-none`}
    >
      {text}
    </div>
  </div>
);

const AddressFormForPanel = ({ onClose }) => {
  // Hook para guardar la dirección
  const { createAddress } = useUserAddresses();

  // Estados para la imagen del mapa
  const [mapImageBlob, setMapImageBlob] = useState(null);
  const [mapImageUrl, setMapImageUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Hook de Google Maps (sin zona extendida)
  const {
    isMapsInitialized,
    autocompleteService,
    placesService,
    validateLocation,
    fetchStaticMapImage,
    uploadMapImageToSupabase,
  } = useGoogleMaps(primaryCoveragePolygon, null, true); // null para extendedPolygon

  // Estados del formulario
  const [panelInput, setPanelInput] = useState("");
  const [panelSuggestions, setPanelSuggestions] = useState([]);
  const [panelSelectedSuggestion, setPanelSelectedSuggestion] = useState(null);
  const [panelCoordinates, setPanelCoordinates] = useState(null);
  const [panelAddressDetails, setPanelAddressDetails] = useState("");
  const [panelHasDetails, setPanelHasDetails] = useState(true);
  const [panelIsValid, setPanelIsValid] = useState(null);
  const [panelIsLoading, setPanelIsLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Estados para la zona y distancia
  const [panelZone, setPanelZone] = useState(null);
  const [panelDistanceKm, setPanelDistanceKm] = useState(null);

  // Estados para el envío
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const inputRef = useRef(null);

  // Función para buscar sugerencias
  const fetchPanelSuggestions = useCallback(() => {
    if (
      !isMapsInitialized ||
      !autocompleteService ||
      panelInput.length <= 2 ||
      panelSelectedSuggestion
    ) {
      setPanelSuggestions([]);
      setPanelIsLoading(false);
      return;
    }

    setPanelIsLoading(true);

    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(10.8, -74.9),
      new window.google.maps.LatLng(11.1, -74.7)
    );

    const request = {
      input: panelInput,
      componentRestrictions: { country: "CO" },
      locationRestriction: bounds,
      types: ["geocode", "establishment"],
    };

    autocompleteService.getPlacePredictions(request, (results, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        Array.isArray(results)
      ) {
        const zonaPermitida = ["barranquilla", "puerto colombia"];
        const zonaProhibida = [
          "soledad",
          "malambo",
          "sabanalarga",
          "baranoa",
          "tubará",
          "tubara",
          "galapa",
        ];

        const filtered = results.filter((p) => {
          if (!p?.description) return false;
          const desc = p.description.toLowerCase();
          const tienePermitida = zonaPermitida.some((z) => desc.includes(z));
          const tieneProhibida = zonaProhibida.some((z) => desc.includes(z));
          return tienePermitida && !tieneProhibida;
        });

        setPanelSuggestions(filtered);
      } else {
        setPanelSuggestions([]);
      }
      setPanelIsLoading(false);
    });
  }, [
    isMapsInitialized,
    autocompleteService,
    panelInput,
    panelSelectedSuggestion,
  ]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    if (panelInput.length > 2) {
      setPanelIsLoading(true);

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      searchTimeoutRef.current = setTimeout(() => {
        if (!panelSelectedSuggestion) {
          fetchPanelSuggestions();
        } else {
          setPanelIsLoading(false);
        }
      }, 2500);
    } else {
      setPanelSuggestions([]);
      setPanelIsLoading(false);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [panelInput, panelSelectedSuggestion, fetchPanelSuggestions]);

  // Limpiar URL de imagen al desmontar
  useEffect(() => {
    return () => {
      if (mapImageUrl) {
        URL.revokeObjectURL(mapImageUrl);
      }
    };
  }, [mapImageUrl]);

  // Manejador del input
  const handlePanelInputChange = (e) => {
    const value = e.target.value;
    setPanelInput(value);

    if (panelSelectedSuggestion) {
      setPanelSelectedSuggestion(null);
      setPanelCoordinates(null);
      setPanelIsValid(null);
      setPanelZone(null);
      setPanelDistanceKm(null);
      setPanelAddressDetails("");
      setPanelHasDetails(true);
      setFormError(null);
    }
  };

  // Limpiar todo el formulario
  const clearPanelAddress = () => {
    setPanelInput("");
    setPanelSuggestions([]);
    setPanelSelectedSuggestion(null);
    setPanelCoordinates(null);
    setPanelIsValid(null);
    setPanelZone(null);
    setPanelDistanceKm(null);
    setPanelAddressDetails("");
    setPanelHasDetails(true);
    setPanelIsLoading(false);
    setFormError(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (mapImageUrl) {
      URL.revokeObjectURL(mapImageUrl);
      setMapImageUrl(null);
    }
    setMapImageBlob(null);
  };

  // Validar ubicación (actualizado para usar el nuevo sistema de zonas)
  const validatePanelLocation = useCallback(
    (coordinates) => {
      if (!isMapsInitialized || !coordinates || !validateLocation) {
        console.warn("validatePanelLocation: No se puede validar.");
        setPanelIsValid(false);
        return false;
      }

      // validateLocation ahora devuelve { isValid, zone, distanceKm, reason }
      const validationResult = validateLocation(coordinates);

      setPanelIsValid(validationResult.isValid);
      setPanelZone(validationResult.zone);
      setPanelDistanceKm(validationResult.distanceKm);

      if (!validationResult.isValid) {
        toast.error(
          "La dirección seleccionada está fuera de nuestra zona de cobertura.",
          { duration: 4000 }
        );
      }

      return validationResult.isValid;
    },
    [isMapsInitialized, validateLocation]
  );

  // Manejador de clic en sugerencia
  const handlePanelSuggestionClick = useCallback(
    async (suggestion) => {
      if (!suggestion?.place_id || !isMapsInitialized || !placesService) {
        console.error("Places service not ready or invalid suggestion");
        toast.error("Error interno al seleccionar la dirección.");
        return;
      }

      setFormError(null);
      setPanelInput(suggestion.description);
      setPanelSelectedSuggestion(suggestion);
      setPanelSuggestions([]);
      setPanelCoordinates(null);
      setPanelIsValid(null);
      setPanelZone(null);
      setPanelDistanceKm(null);
      setPanelIsLoading(true);

      if (mapImageUrl) {
        URL.revokeObjectURL(mapImageUrl);
        setMapImageUrl(null);
      }
      setMapImageBlob(null);

      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ["geometry", "name", "formatted_address"],
        },
        async (place, status) => {
          let valid = false;
          try {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place?.geometry?.location
            ) {
              const coords = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              };
              setPanelCoordinates(coords);
              valid = validatePanelLocation(coords);

              if (valid) {
                try {
                  setIsImageLoading(true);
                  const imageResult = await fetchStaticMapImage(coords);
                  if (imageResult && imageResult.blob) {
                    setMapImageBlob(imageResult.blob);
                    const imageUrl = URL.createObjectURL(imageResult.blob);
                    setMapImageUrl(imageUrl);
                  }
                } catch (error) {
                  console.error("Error obteniendo imagen del mapa:", error);
                } finally {
                  setIsImageLoading(false);
                }
              }
            } else {
              console.error("Error fetching place details:", status);
              setPanelCoordinates(null);
              setPanelIsValid(false);
              toast.error(
                "No se pudieron obtener los detalles de la dirección.",
                { duration: 3000 }
              );
            }
          } catch (error) {
            console.error("Error in getDetails callback:", error);
            setPanelCoordinates(null);
            setPanelIsValid(false);
          } finally {
            setPanelIsLoading(false);
          }
        }
      );
    },
    [
      isMapsInitialized,
      placesService,
      validatePanelLocation,
      fetchStaticMapImage,
      mapImageUrl,
    ]
  );

  // Guardar dirección
  const handleSaveAddress = async () => {
    if (
      !panelSelectedSuggestion ||
      !panelCoordinates ||
      panelIsValid !== true
    ) {
      const errorMsg = !panelSelectedSuggestion
        ? "Busca y selecciona una dirección de la lista."
        : panelIsValid === false
        ? "La dirección seleccionada está fuera de cobertura."
        : "Espera a que se obtengan y validen los detalles de la dirección.";
      setFormError(errorMsg);
      toast.error(errorMsg, { duration: 3500 });
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      let imageUrl = null;
      if (mapImageBlob) {
        const uploadResult = await uploadMapImageToSupabase(
          mapImageBlob,
          panelCoordinates
        );
        imageUrl = uploadResult;
      }

      const newAddress = await createAddress({
        address: panelSelectedSuggestion.description,
        latitude: panelCoordinates.lat,
        longitude: panelCoordinates.lng,
        details: panelHasDetails ? panelAddressDetails : "",
        image_url: imageUrl,
        // Agregar zona y distancia si tu sistema los necesita
        zone: panelZone,
        distance_km: panelDistanceKm,
      });

      if (newAddress) {
        toast.success("Dirección guardada y seleccionada!");
        onClose();
      } else {
        throw new Error("No se pudo guardar la dirección.");
      }
    } catch (error) {
      console.error("Error al guardar dirección:", error);
      const displayError =
        "Hubo un problema al guardar la dirección. Intenta de nuevo.";
      setFormError(displayError);
      toast.error(displayError, { duration: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Obtener información de la zona actual
  const getCurrentZoneInfo = () => {
    if (!panelZone || !DELIVERY_ZONES[panelZone]) return null;
    return DELIVERY_ZONES[panelZone];
  };

  return (
    <div className="p-4 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto no-scrollbar pr-2">
        {/* Input de búsqueda */}
        {!panelSelectedSuggestion || panelIsValid !== true ? (
          <div className="mb-4">
            <label className="text-md text-gray-800 mb-1 block">
              Buscar dirección
            </label>
            <div className="relative flex gap-2 items-center">
              <input
                ref={inputRef}
                className="flex-1 p-4 border bg-gray-50 pr-8 text-lg disabled:opacity-50 rounded-lg"
                type="text"
                placeholder="Ingresa tu dirección o un lugar"
                value={panelInput}
                onChange={handlePanelInputChange}
                disabled={
                  isSubmitting || (panelIsLoading && panelSelectedSuggestion)
                }
              />
              {panelIsLoading && !isSubmitting && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <Loader className="animate-spin text-blue-500" size={20} />
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* Lista de sugerencias */}
        {panelSuggestions.length > 0 && !panelSelectedSuggestion && (
          <div className="">
            {panelSuggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className="p-2 hover:bg-gray-100 cursor-pointer flex items-start gap-2 border-b border-gray-100 last:border-b-0 rounded-xl"
                onClick={() => handlePanelSuggestionClick(suggestion)}
              >
                <MapPinIcon className="w-5 h-5 text-gray-500 mt-1 shrink-0" />
                <span>{suggestion.description}</span>
              </div>
            ))}
          </div>
        )}

        {/* Vista de dirección seleccionada */}
        {panelSelectedSuggestion && panelIsValid === true && (
          <div className="mb-4 space-y-2">
            <div className="flex flex-col gap-4">
              <div className="flex gap-2 items-center justify-between">
                <div className="flex justify-between items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-500">
                    <MapPinIcon className="w-5 h-5 mt-0.5" />
                  </div>
                  <span>Dirección seleccionada</span>
                </div>
                <button
                  type="button"
                  className="text-red-600 text-sm flex items-center justify-center"
                  onClick={clearPanelAddress}
                  disabled={isSubmitting || panelIsLoading}
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Imagen del mapa */}
              <div className="my-4">
                <div className="rounded-md overflow-hidden border border-gray-200">
                  {isImageLoading || panelIsLoading ? (
                    <div className="h-40 bg-gray-100 flex items-center justify-center animate-pulse">
                      <MapPin className="text-gray-300 w-12 h-12" />
                    </div>
                  ) : mapImageUrl ? (
                    <img
                      src={mapImageUrl}
                      alt="Vista previa de la ubicación"
                      className="w-full h-40 object-cover"
                    />
                  ) : (
                    <div className="h-40 bg-gray-50 flex items-center justify-center">
                      <MapPin className="text-gray-300 w-10 h-10" />
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xl font-medium text-gray-800">
                {panelSelectedSuggestion.description}
              </p>

              {/* Badge de zona y costo de entrega */}
              {getCurrentZoneInfo() && (
                <>
                  <Badge
                    text={getCurrentZoneInfo().label}
                    bgColor={getCurrentZoneInfo().bgColor}
                    textColor={getCurrentZoneInfo().textColor}
                  />
                </>
              )}
            </div>

            <DividerComponent
              color="#eeeeee"
              direction="horizontal"
              margin={32}
              size={1}
            />
          </div>
        )}

        {/* Mensaje de error si está fuera de cobertura */}
        {panelSelectedSuggestion && panelIsValid === false && (
          <div className="mb-4">
            <div className="flex items-center gap-3 bg-red-100 p-4 py-2 rounded-xl">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-red-200 text-red-500">
                <MapPinIcon className="w-5 h-5 mt-0.5" />
              </div>
              <div>
                <p className="text-sm text-red-600 mt-1">
                  Fuera de cobertura. No podemos realizar envíos a esta
                  ubicación.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sección de detalles adicionales */}
        {panelSelectedSuggestion && panelIsValid === true && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <label className=" text-md text-gray-800">
                Detalles adicionales
              </label>
              <div className="flex items-center gap-2">
                <span className="text-base text-gray-600">
                  {panelHasDetails ? "Añadir detalles" : "Sin detalles"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPanelHasDetails((prev) => !prev);
                    if (panelHasDetails) setPanelAddressDetails("");
                  }}
                  disabled={isSubmitting}
                  className={`w-10 h-5 rounded-full relative transition-colors ${
                    panelHasDetails ? "bg-blue-500" : "bg-gray-300"
                  } ${
                    isSubmitting
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <div
                    className={`absolute w-4 h-4 rounded-full bg-white top-0.5 transition-all duration-200 ${
                      panelHasDetails ? "left-5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>

            {panelHasDetails && (
              <textarea
                className="w-full p-2 border rounded-md bg-gray-50 resize-none disabled:opacity-70"
                placeholder="Apartamento, oficina, casa, número, puntos de referencia..."
                rows="3"
                value={panelAddressDetails}
                onChange={(e) => setPanelAddressDetails(e.target.value)}
                disabled={isSubmitting}
              />
            )}
          </div>
        )}

        {/* Mensaje de error del formulario */}
        {formError && (
          <div className="my-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
            {formError}
          </div>
        )}
      </div>

      {/* Botones de acción */}
      <div className="mt-auto pt-4 border-t border-gray-200 flex justify-end gap-3">
        <Button
          variant="soft"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-lg rounded-xl"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSaveAddress}
          disabled={
            !panelSelectedSuggestion ||
            !panelCoordinates ||
            panelIsValid !== true ||
            isSubmitting ||
            panelIsLoading
          }
          className={`px-4 py-2 text-lg rounded-xl ${
            !panelSelectedSuggestion ||
            !panelCoordinates ||
            panelIsValid !== true ||
            panelIsLoading
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-gray-800 hover:bg-gray-900"
          } text-white transition-colors min-w-[140px]`}
        >
          {isSubmitting ? (
            <>
              <Loader className="animate-spin mr-2 h-4 w-4" />
              Guardando...
            </>
          ) : (
            "Guardar dirección"
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddressFormForPanel;
